<template>
  <el-dialog title="过磅进厂传输SAP确认" :visible.sync="visible" :close-on-click-modal="false" width="800px" @close="onCancel">
    <span>确认传输SAP吗？</span>
    <el-form ref="form" :model="form" label-width="120px" :rules="rules">
      <el-form-item v-if="showEditableStoragePoint" :label="storagePointLabel" prop="storage_point_id">
        <el-select class="w-96" filterable v-model="form.storage_point_id" :placeholder="storagePointPlaceholder" @change="onStoragePointChange">
          <el-option
            v-for="item in storagePointList"
            :key="item.storage_point_id"
            :label="getStoragePointOptionLabel(item)"
            :value="item.storage_point_id"
          ></el-option>
        </el-select>
      </el-form-item>
      <el-form-item v-else-if="showReadonlyStoragePoint" :label="storagePointLabel">
        <span>{{ currentStoragePointDisplay }}</span>
      </el-form-item>
      <el-form-item v-if="showPutAreaField" label="放柜区域" prop="put_area">
        <el-select class="w-96" v-model="form.put_area" placeholder="请选择">
          <el-option v-for="item in areaOption" :key="item.value" :label="item.label" :value="item.value"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item v-if="showContainerNumberField" label="集装箱号" prop="container_number" :rules="[
        { required: isContainerNumberRequired, message: '请输入集装箱号', trigger: 'blur' },
        { validator: this.validateContainerNumber, trigger: 'blur' }
      ]">
        <el-input class="w-96" v-model="form.container_number" placeholder="请输入集装箱号"></el-input>
      </el-form-item>
      <el-form-item label="备注说明" prop="remark">
        <el-input class="w-96" type="textarea" v-model="form.remark" placeholder="请输入备注说明"></el-input>
      </el-form-item>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="onCancel">取 消</el-button>
      <el-button type="primary" @click="onConfirm">确认传输</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { debounce } from 'lodash';
export default {
  data() {
    return {
      vehicle_type: 0,
      visible: false,
      form: this.getDefaultForm(),
      rules: {},
      areaOption: [],
      storagePointList: []
    };
  },
  computed: {
    isHuafu() {
      return this.form.order_type === "NB";
    },
    showEditableStoragePoint() {
      return !this.isHuafu;
    },
    // 化辅过磅传 SAP 直接沿用预约卸货点，页面只做只读回显，不允许现场改写。
    showReadonlyStoragePoint() {
      return this.isHuafu;
    },
    // 化辅过磅接口不接收放柜区域，避免页面展示提交后会被丢弃的无效字段。
    showPutAreaField() {
      return !this.isHuafu;
    },
    // 集装箱号只对集装箱车辆展示，并继续沿用必填与格式校验规则。
    showContainerNumberField() {
      return Number(this.vehicle_type) === 7;
    },
    // 化辅集装箱车型无需提供箱号，其他业务继续保持集装箱车辆必填规则。
    isContainerNumberRequired() {
      return !this.isHuafu && Number(this.vehicle_type) === 7;
    },
    // 现场统一按“存储地点”展示，避免化辅和其它业务在过磅弹窗里出现两套叫法。
    storagePointLabel() {
      return "存储地点";
    },
    // 占位文案与字段名称保持一致，避免误导用户理解为另一套业务字段。
    storagePointPlaceholder() {
      return "请选择存储地点";
    },
    // 只读回显优先展示“编码-名称”维护值，缺失时再退回 SAP 编码，避免现场误以为字段丢失。
    currentStoragePointDisplay() {
      return (
        this.form.storage_point_name ||
        this.form.storage_point_id ||
        "--"
      );
    }
  },
  created() {
    this.getPutArea();
  },
  methods: {
    // 返回默认表单，保证每次打开弹窗都能得到稳定的初始值。
    getDefaultForm() {
      return {
        id: "",
        car_business_id: "",
        order_type: "",
        put_area: "",
        container_number: "",
        remark: "",
        storage_point_id: "",
        storage_point_name: "",
        warehouse_id: "",
        warehouse_name: "",
        order_item: "",
        package_count: "",
        is_small_package: "",
        carrier_type: ""
      };
    },
    // 打开弹窗时直接使用列表已补齐的预约和排队事实，避免详情空值覆盖集装箱号。
    onOpen(row) {
      this.storagePointList = [];
      this.vehicle_type = row.vehicle_type || 0;
      this.form = this.buildFormFromRow(row);
      this.visible = true;
      if (this.shouldLoadStoragePoint()) {
        this.getStoragePoint();
      }
    },
    // 化辅进厂传 SAP 不允许改存储地点，其它业务继续走原有下拉选择。
    shouldLoadStoragePoint() {
      return !this.isHuafu;
    },
    // 统一根据列表行组装弹窗表单，集中处理预约、排队和过磅字段的默认值。
    buildFormFromRow(row) {
      const queueRow = row.booking_queue || {};
      const defaultStoragePointId = this.getDefaultStoragePointId(row, queueRow);
      const defaultStoragePointName = this.getDefaultStoragePointName(row, queueRow);
      return Object.assign(this.getDefaultForm(), {
        id: row.id || "",
        car_business_id: queueRow.car_business_id || row.car_business_id || "",
        order_type: row.order_type || "",
        put_area: row.put_area || "",
        container_number: row.container_number || "",
        remark: row.remark || "",
        storage_point_id: this.normalizeStoragePointId(defaultStoragePointId),
        storage_point_name: defaultStoragePointName,
        warehouse_id: queueRow.warehouse_id || row.warehouse_id || "",
        warehouse_name: queueRow.warehouse_name || row.warehouse_name || "",
        order_item: row.order_item || "",
        package_count:
          row.package_count === null || row.package_count === undefined
            ? ""
            : String(row.package_count),
        // 国废“小包”字段统一沿用 SAP 的空串 / X 口径，避免再次打开弹窗时被旧 0/1 转换污染。
        is_small_package: row.is_small_package === "X" ? "X" : "",
        carrier_type:
          row.carrier_type === null || row.carrier_type === undefined || row.carrier_type === ""
            ? ""
            : String(row.carrier_type)
      });
    },
    // 统一把存储地点编码转成字符串，避免 el-select 因数字/字符串类型不一致导致回显异常。
    normalizeStoragePointId(value) {
      return value === null || value === undefined || value === ""
        ? ""
        : String(value);
    },
    // 化辅优先沿用预约卸货点，其它业务继续沿用已有 SAP 存储地点事实。
    getDefaultStoragePointId(row, queueRow) {
      if (row.order_type === "NB") {
        const storagePoint = this.buildHuafuStoragePointOption({
          storage_point_id: queueRow.storage_point_id || row.storage_point_id || "",
          storage_point_name: queueRow.storage_point_name || row.storage_point_name || "",
          warehouse_id: queueRow.warehouse_id || row.warehouse_id || "",
          warehouse_name: queueRow.warehouse_name || row.warehouse_name || ""
        });
        return storagePoint.storage_point_id;
      }
      return queueRow.storage_point_id || row.storage_point_id || "";
    },
    // 化辅优先沿用预约卸货点名称，其它业务继续沿用已有 SAP 存储地点名称。
    getDefaultStoragePointName(row, queueRow) {
      if (row.order_type === "NB") {
        const storagePoint = this.buildHuafuStoragePointOption({
          storage_point_id: queueRow.storage_point_id || row.storage_point_id || "",
          storage_point_name: queueRow.storage_point_name || row.storage_point_name || "",
          warehouse_id: queueRow.warehouse_id || row.warehouse_id || "",
          warehouse_name: queueRow.warehouse_name || row.warehouse_name || ""
        });
        return storagePoint.storage_point_name;
      }
      return queueRow.storage_point_name || row.storage_point_name || "";
    },
    // 化辅存储地点展示按 SAP “编码-名称” 维护，提交时只传前半段编码。
    buildHuafuStoragePointOption(option = {}) {
      const storagePointName =
        option.storage_point_name ||
        option.storage_point_label ||
        option.warehouse_name ||
        "";
      const storagePointId = this.extractHuafuStoragePointCode(
        storagePointName,
        option.storage_point_id || option.warehouse_id || ""
      );
      return {
        ...option,
        storage_point_id: this.normalizeStoragePointId(storagePointId),
        storage_point_name: storagePointName,
        storage_point_label: storagePointName
      };
    },
    // 关键步骤：从“编码-名称”中拆出四位 SAP 编码，内部装卸点 UUID 不能作为旧值兜底。
    extractHuafuStoragePointCode(displayValue, fallbackValue = "") {
      const normalizedDisplayValue = (displayValue || "").trim();
      const matched = normalizedDisplayValue.match(/^([A-Za-z0-9]{4})\s*-\s*.+$/u);
      if (matched && matched[1]) {
        return matched[1].trim();
      }
      const normalizedFallbackValue = (fallbackValue || "").trim();
      return /^[A-Za-z0-9]{4}$/.test(normalizedFallbackValue)
        ? normalizedFallbackValue
        : "";
    },
    // 化辅下拉展示装卸点名称，普通业务继续展示 SAP 编码加名称。
    getStoragePointOptionLabel(item) {
      if (this.isHuafu) {
        return item.storage_point_label || item.storage_point_name || "";
      }
      return `${item.storage_point_id}（${item.storage_point_name}）`;
    },
    onStoragePointChange(value) {
      const storagePoint = this.storagePointList.find(
        item => item.storage_point_id === this.normalizeStoragePointId(value)
      );
      this.form.storage_point_name = storagePoint
        ? storagePoint.storage_point_name
        : "";
    },
    // 校验集装箱号格式，允许空值，但非空时必须是 4 位字母加 7 位数字。
    validateContainerNumber(rule, value, callback) {
      const normalizedValue = String(value || "")
        .trim()
        .toUpperCase();
      if (!normalizedValue) {
        callback();
        return;
      }
      if (!/^[A-Z]{4}\d{7}$/.test(normalizedValue)) {
        callback(new Error("集装箱号需为4位字母+7位数字"));
        return;
      }
      callback();
    },
    onCancel() {
      this.form = this.getDefaultForm();
      this.visible = false;
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
      });
    },
    // 查询放柜区域列表，兼容木片与国废业务共用该基础数据。
    async getPutArea() {
      try {
        const dataObj = await this.$common.http({
          url: "/api/sap/query_put_area"
        });
        this.areaOption = dataObj.data.data.map(item => {
          return {
            ...item,
            label: `${item.put_area_name}（${item.put_area_code}）`,
            value: item.put_area_id
          };
        });
      } catch (error) { }
    },
    // 关键步骤：只有非化辅业务才加载可选存储地点，避免现场在 NB 弹窗里改写预约卸货点事实。
    async getStoragePoint() {
      try {
        if (this.isHuafu && !this.form.car_business_id) {
          this.storagePointList = this.appendBookedHuafuStoragePointOption([]);
          this.syncStoragePointSelection();
          return;
        }
        const requestConfig = this.isHuafu
          ? {
            url: "/api/queue/dispatcher/query_warehouse_by_business_id",
            data: {
              car_business_id: this.form.car_business_id
            }
          }
          : {
            url: "/api/queue/dispatcher/query_storage_point"
          };
        const dataObj = await this.$common.http({
          ...requestConfig
        });
        const storagePointList = dataObj.data.data || [];
        this.storagePointList = this.isHuafu
          ? this.appendBookedHuafuStoragePointOption(
              storagePointList.map(item => this.buildHuafuStoragePointOption(item))
            )
          : storagePointList.map(item => ({
              ...item,
              storage_point_id: this.normalizeStoragePointId(item.storage_point_id)
            }));
        this.syncStoragePointSelection();
      } catch (error) {
        if (this.isHuafu) {
          this.storagePointList = this.appendBookedHuafuStoragePointOption([]);
          this.syncStoragePointSelection();
        }
      }
    },
    // 关键步骤：即使业务类型装卸点接口暂时没回当前预约点，也要保证已预约的存储地点能在弹窗里回显和可选。
    appendBookedHuafuStoragePointOption(storagePointList) {
      if (!this.isHuafu) {
        return storagePointList;
      }
      const bookedStoragePoint = this.buildHuafuStoragePointOption({
        storage_point_id: this.form.storage_point_id || "",
        storage_point_name: this.form.storage_point_name || "",
        warehouse_id: this.form.warehouse_id || "",
        warehouse_name: this.form.warehouse_name || ""
      });
      const bookedStoragePointId = bookedStoragePoint.storage_point_id;
      const bookedStoragePointName = bookedStoragePoint.storage_point_name;
      if (!bookedStoragePointId || !bookedStoragePointName) {
        return storagePointList;
      }
      const exists = storagePointList.some(
        item => item.storage_point_id === bookedStoragePointId
      );
      if (exists) {
        return storagePointList;
      }
      return [bookedStoragePoint, ...storagePointList];
    },
    // 打开弹窗后把默认值对齐到当前可选项，避免旧事实和最新配置不一致时页面显示异常。
    syncStoragePointSelection() {
      if (!this.storagePointList.length) {
        return;
      }
      this.form.storage_point_id = this.normalizeStoragePointId(
        this.form.storage_point_id
      );
      const matchedStoragePoint = this.storagePointList.find(
        item => item.storage_point_id === this.form.storage_point_id
      );
      if (matchedStoragePoint) {
        this.form.storage_point_id = matchedStoragePoint.storage_point_id;
        this.form.storage_point_name = matchedStoragePoint.storage_point_name;
        return;
      }
      if (this.isHuafu) {
        const matchedWarehousePoint = this.storagePointList.find(
          item => item.warehouse_id === this.form.warehouse_id
        );
        if (matchedWarehousePoint) {
          this.form.storage_point_id = matchedWarehousePoint.storage_point_id;
          this.form.storage_point_name = matchedWarehousePoint.storage_point_name;
        }
      }
    },
    onConfirm() {
      this.$refs.form.validate(async valid => {
        if (valid) {
          this.$confirm("确认传输SAP吗？", "过磅进厂传输SAP确认").then(() => {
            this.onSubmit();
          });
        }
      });
    },
    // 提交时直接把国废扩展字段一起带给后端，后端会落库后再组装新协议。
    onSubmit: debounce(async function() {
      this.$common.pcToastLoading('正在传输SAP，请稍等...');
      try {
        // 关键步骤：提交前统一转成大写并去掉首尾空格，避免同一箱号因大小写差异被重复录入。
        this.form.container_number = String(this.form.container_number || "")
          .trim()
          .toUpperCase();
        const putAreaOption = this.areaOption.find((e) => e.value === this.form.put_area);
        const result = await this.$common.http({
          url: "/api/queue/admin/push_sap",
          data: {
            ...this.form,
            // 关键步骤：化辅允许在业务类型关联的装卸点范围内改选存储地点，但仍不带放柜区域。
            storage_point_id: this.form.storage_point_id,
            storage_point_name: this.form.storage_point_name,
            put_area: this.isHuafu ? "" : this.form.put_area,
            put_area_name: this.isHuafu ? "" : (putAreaOption ? putAreaOption.label : '')
          }
        });
        if (result.data.data.flag == 1) {
          this.$message.success("SAP数据已发送");
        } else {
          this.$message.error(result.data.data.message);
        }
        this.$emit("ok");
        this.onCancel();
        setTimeout(() => {
          this.$common.pcToastClose();
        }, (1500));
      } catch (error) {
        this.$common.pcToastClose();
      }
  }, 300)
  }
};
</script>

<style lang="less" scoped>
/deep/ .el-dialog__body {
  padding: 10px 15px 0px !important;
}
</style>
